import { collection, doc, setDoc, getDocs, query, where, getFirestore, writeBatch } from "firebase/firestore";
import * as XLSX from "xlsx";
import anexoTablasFile from "../views/planillas/Anexo2-Tablas260510.xls";
import firebaseApp from "../firebase/credenciales";

const db = getFirestore(firebaseApp);

export const cargarTabla10Ocupaciones = async () => {
  try {
    const response = await fetch(anexoTablasFile);
    if (!response.ok) {
      throw new Error("No se pudo leer el archivo Anexo2-Tablas260510.xls");
    }

    const buffer = await response.arrayBuffer();
    const workbook = XLSX.read(buffer, { type: "array" });
    const sheet = workbook.Sheets["T10 Ocupaciones"];
    if (!sheet) {
      throw new Error("No se encontró la hoja 'T10 Ocupaciones' en el archivo.");
    }

    const rows = XLSX.utils.sheet_to_json(sheet, { header: 1, defval: "" });
    const ocupaciones = rows
      .slice(3) // Salta título, línea vacía y cabecera
      .map((row) => ({
        codigo: String(row?.[0] || "").trim(),
        descripcion: String(row?.[1] || "").trim(),
      }))
      .filter((item) => /^\d{6}$/.test(item.codigo) && item.descripcion);

    if (ocupaciones.length === 0) {
      throw new Error("No se encontraron ocupaciones válidas en la hoja T10.");
    }

    let batch = writeBatch(db);
    let batchCount = 0;
    let total = 0;

    for (const item of ocupaciones) {
      const ref = doc(db, "pl_tabla10_ocupaciones", item.codigo);
      batch.set(ref, {
        codigo: item.codigo,
        descripcion: item.descripcion,
        estado: "ACTIVO",
        fuente: "SUNAT_T10",
        updated_at: new Date(),
      }, { merge: true });
      batchCount += 1;
      total += 1;

      if (batchCount >= 450) {
        await batch.commit();
        batch = writeBatch(db);
        batchCount = 0;
      }
    }

    if (batchCount > 0) {
      await batch.commit();
    }

    return {
      success: true,
      message: `Tabla 10 cargada correctamente en pl_tabla10_ocupaciones (${total} registros).`,
      total,
    };
  } catch (error) {
    console.error("Error al cargar Tabla 10 Ocupaciones:", error);
    return {
      success: false,
      message: "Error al cargar Tabla 10 Ocupaciones",
      error: error.message,
    };
  }
};

export const initializeCollections = async (rucEmpresa) => {
  try {
    // 1. Crear conceptos básicos de nómina
    const conceptosBasicos = [
      // Ingresos Básicos
      {
        codigo: "0101",
        descripcion: "SUELDO BÁSICO",
        tipo: "INGRESO",
        afecto: {
          quinta: true,
          essalud: true,
          onp: true,
          afp: true
        },
        formula: "BASICO",
        plame_tipo: "0601",
        ruc_empresa: rucEmpresa
      },
      {
        codigo: "0201",
        descripcion: "ASIGNACIÓN FAMILIAR",
        tipo: "INGRESO",
        afecto: {
          quinta: true,
          essalud: true,
          onp: true,
          afp: true
        },
        formula: "MIN_VITAL * 0.10",
        plame_tipo: "0601",
        ruc_empresa: rucEmpresa
      },
      // Beneficios Sociales
      {
        codigo: "0301",
        descripcion: "GRATIFICACIÓN",
        tipo: "INGRESO",
        afecto: {
          quinta: true,
          essalud: false,
          onp: false,
          afp: false
        },
        formula: "BASICO * 1",
        plame_tipo: "0602",
        ruc_empresa: rucEmpresa
      },
      {
        codigo: "0302",
        descripcion: "CTS",
        tipo: "INGRESO",
        afecto: {
          quinta: false,
          essalud: false,
          onp: false,
          afp: false
        },
        formula: "(BASICO + ASIG_FAM) * 1.1667",
        plame_tipo: "0603",
        ruc_empresa: rucEmpresa
      },
      {
        codigo: "0303",
        descripcion: "VACACIONES",
        tipo: "INGRESO",
        afecto: {
          quinta: true,
          essalud: true,
          onp: true,
          afp: true
        },
        formula: "BASICO * 1",
        plame_tipo: "0604",
        ruc_empresa: rucEmpresa
      },
      // Descuentos AFP
      {
        codigo: "0501",
        descripcion: "AFP APORTE OBLIGATORIO",
        tipo: "DESCUENTO",
        afecto: {
          quinta: false,
          essalud: false,
          onp: false,
          afp: false
        },
        formula: "BASE_AFP * AFP_FONDO",
        plame_tipo: "0605",
        ruc_empresa: rucEmpresa
      },
      {
        codigo: "0502",
        descripcion: "AFP SEGURO",
        tipo: "DESCUENTO",
        afecto: {
          quinta: false,
          essalud: false,
          onp: false,
          afp: false
        },
        formula: "BASE_AFP * AFP_SEGURO",
        plame_tipo: "0605",
        ruc_empresa: rucEmpresa
      },
      {
        codigo: "0503",
        descripcion: "AFP COMISIÓN",
        tipo: "DESCUENTO",
        afecto: {
          quinta: false,
          essalud: false,
          onp: false,
          afp: false
        },
        formula: "BASE_AFP * AFP_COMISION",
        plame_tipo: "0605",
        ruc_empresa: rucEmpresa
      },
      // Descuento ONP
      {
        codigo: "0601",
        descripcion: "ONP",
        tipo: "DESCUENTO",
        afecto: {
          quinta: false,
          essalud: false,
          onp: false,
          afp: false
        },
        formula: "BASE_ONP * 0.13",
        plame_tipo: "0601",
        ruc_empresa: rucEmpresa
      },
      // Aportes Empleador
      {
        codigo: "0701",
        descripcion: "ESSALUD",
        tipo: "APORTE",
        afecto: {
          quinta: false,
          essalud: false,
          onp: false,
          afp: false
        },
        formula: "BASE_ESSALUD * 0.09",
        plame_tipo: "0607",
        ruc_empresa: rucEmpresa
      },
      {
        codigo: "0702",
        descripcion: "SEGURO VIDA LEY",
        tipo: "APORTE",
        afecto: {
          quinta: false,
          essalud: false,
          onp: false,
          afp: false
        },
        formula: "SEGURO_VIDA",
        plame_tipo: "0608",
        ruc_empresa: rucEmpresa
      },
      {
        codigo: "0703",
        descripcion: "SCTR SALUD",
        tipo: "APORTE",
        afecto: {
          quinta: false,
          essalud: false,
          onp: false,
          afp: false
        },
        formula: "BASE_SCTR * TASA_SCTR_SALUD",
        plame_tipo: "0609",
        ruc_empresa: rucEmpresa
      },
      {
        codigo: "0704",
        descripcion: "SCTR PENSIÓN",
        tipo: "APORTE",
        afecto: {
          quinta: false,
          essalud: false,
          onp: false,
          afp: false
        },
        formula: "BASE_SCTR * TASA_SCTR_PENSION",
        plame_tipo: "0610",
        ruc_empresa: rucEmpresa
      },
      // Régimen Especial Minero
      {
        codigo: "0801",
        descripcion: "AFP RÉGIMEN MINERO",
        tipo: "DESCUENTO",
        afecto: {
          quinta: false,
          essalud: false,
          onp: false,
          afp: false
        },
        formula: "BASE_AFP * AFP_TASA_MINERA",
        plame_tipo: "0611",
        ruc_empresa: rucEmpresa
      }
    ];

    // Crear cada concepto en Firestore con nuevo prefijo pl_
    for (const concepto of conceptosBasicos) {
      await setDoc(doc(db, "pl_conceptos_nomina", `${rucEmpresa}_${concepto.codigo}`), concepto);
    }

    // 2. Migrar usuarios actuales a la colección empleados
    const usersQuery = query(
      collection(db, "users"),
      where("rucEmpresa", "==", rucEmpresa),
    );

    const usersSnapshot = await getDocs(usersQuery);

    // Verificar primero si ya existen empleados
    const empleadosExistentes = new Set();
    const empleadosQuery = query(
      collection(db, "pl_empleados"),
      where("ruc_empresa", "==", rucEmpresa)
    );
    const empleadosSnapshot = await getDocs(empleadosQuery);
    empleadosSnapshot.forEach(doc => empleadosExistentes.add(doc.data().dni));

    // Crear nuevos empleados solo si no existen
    for (const userDoc of usersSnapshot.docs) {
      const userData = userDoc.data();

      // Verificar si el empleado ya existe
      if (!empleadosExistentes.has(userData.dni)) {
        const empleadoData = {
          dni: userData.dni,
          nombres: userData.name,
          apellidos: userData.apellidos,
          fecha_ingreso: new Date(),
          regimen_pensionario: "ONP",
          sueldo_base: 0,
          cuenta_bancaria: "",
          tipo_contrato: "INDEFINIDO",
          ruc_empresa: rucEmpresa,
          asignacion_familiar: false,
          cuspp: "",
          situacion: "ACTIVO",
          estado_civil: "",
          categoria_ocupacional: userData.cargo || "",
          email: userData.email,
          phone: userData.phone || "",
          direccion: userData.direccion || "",
          activo: true,
          created_at: new Date(),
          updated_at: new Date()
        };

        await setDoc(doc(db, "pl_empleados", userData.dni), empleadoData);
      }
    }
    // 3. Crear documento de configuración de planilla
    const configPlanilla = {
      ruc_empresa: rucEmpresa,
      uit: 5500,
      sueldo_minimo: 1130,
      asignacion_familiar: 113.00,
      essalud_vida: 3.00,
      porcentaje_essalud: 0.09,
      porcentaje_onp: 0.13,
      // AFP Integra
      afp_integra: {
        fondo: 0.10,
        seguro: 0.0137,
        comision: 0.0155
      },
      // AFP Prima
      afp_prima: {
        fondo: 0.10,
        seguro: 0.0137,
        comision: 0.0160
      },
      // AFP Profuturo
      afp_profuturo: {
        fondo: 0.10,
        seguro: 0.0137,
        comision: 0.0169
      },
      // AFP Habitat
      afp_habitat: {
        fondo: 0.10,
        seguro: 0.0137,
        comision: 0.0147
      },
      // SCTR por nivel de riesgo
      sctr: {
        riesgo_1: { salud: 0.0063, pension: 0.0060 },
        riesgo_2: { salud: 0.0123, pension: 0.0120 },
        riesgo_3: { salud: 0.0183, pension: 0.0180 },
        riesgo_4: { salud: 0.0243, pension: 0.0240 },
        riesgo_5: { salud: 0.0303, pension: 0.0300 }
      },
      // Vida Ley
      vida_ley: {
        rango_1: { tasa: 0.0053 },
        rango_2: { tasa: 0.0042 },
        rango_3: { tasa: 0.0031 }
      },
      // SENATI
      senati: {
        tasa: 0.0075
      },
      // Otros
      dias_trabajo_mes: 30,
      horas_trabajo_dia: 8,
      created_at: new Date(),
      updated_at: new Date()
    };

    await setDoc(doc(db, "pl_config", rucEmpresa), configPlanilla);

    // 4. Crear colección de planillas
    const planillaInicial = {
      ruc_empresa: rucEmpresa,
      periodo: new Date().toISOString().slice(0, 7), // YYYY-MM
      fecha_inicio: new Date().toISOString().slice(0, 10),
      fecha_fin: new Date().toISOString().slice(0, 10),
      estado: 'BORRADOR',
      tipo_planilla: 'MENSUAL',
      total_ingresos: 0,
      total_descuentos: 0,
      total_neto: 0,
      total_empleados: 0,
      created_at: new Date(),
      updated_at: new Date()
    };

    await setDoc(doc(db, "pl_planillas", `${rucEmpresa}_${planillaInicial.periodo}`), planillaInicial);

    // 5. Crear estructura para detalle de planillas
    const planillaDetalleEstructura = {
      ruc_empresa: rucEmpresa,
      planilla_id: `${rucEmpresa}_${planillaInicial.periodo}`,
      empleado_id: 'EJEMPLO',
      periodo: planillaInicial.periodo,
      dias_trabajados: 30,
      horas_trabajadas: 192,
      ingresos: [],
      descuentos: [],
      aportes: [],
      total_ingresos: 0,
      total_descuentos: 0,
      total_aportes: 0,
      neto_pagar: 0,
      created_at: new Date(),
      updated_at: new Date()
    };

    // Crear documento en la colección pl_planilla_detalle
    await setDoc(
      doc(db, "pl_planilla_detalle", `${rucEmpresa}_${planillaInicial.periodo}_EJEMPLO`),
      planillaDetalleEstructura
    );

    // Usar los empleados ya obtenidos anteriormente en lugar de hacer una nueva consulta
    for (const empleadoDoc of empleadosSnapshot.docs) {
      const empleadoData = empleadoDoc.data();
      const detalleEmpleado = {
        ...planillaDetalleEstructura,
        empleado_id: empleadoDoc.id,
        ingresos: [
          {
            codigo: "0101",
            descripcion: "SUELDO BÁSICO",
            monto: empleadoData.sueldo_base || 0
          }
        ]
      };

      await setDoc(
        doc(db, "pl_planilla_detalle", `${rucEmpresa}_${planillaInicial.periodo}_${empleadoDoc.id}`),
        detalleEmpleado
      );
    }

    // 6. Crear colección de variables globales de planilla
    const variablesGlobales = {
      ruc_empresa: rucEmpresa,
      uit: 5500,
      sueldo_minimo: 1130,
      porcentaje_essalud: 0.09,
      porcentaje_onp: 0.13,
      porcentaje_afp_fondo: 0.10,
      porcentaje_afp_seguro: 0.0174,
      porcentaje_afp_comision: 0.0167,
      created_at: new Date(),
      updated_at: new Date()
    };

    await setDoc(doc(db, "pl_variables", rucEmpresa), variablesGlobales);

    // 7. Crear colección de periodos de planilla
    const periodoPlanilla = {
      ruc_empresa: rucEmpresa,
      periodo: planillaInicial.periodo,
      estado: 'ABIERTO',
      fecha_inicio: planillaInicial.fecha_inicio,
      fecha_fin: planillaInicial.fecha_fin,
      fecha_pago: null,
      created_at: new Date(),
      updated_at: new Date()
    };

    await setDoc(doc(db, "pl_periodos", `${rucEmpresa}_${planillaInicial.periodo}`), periodoPlanilla);

    // 8. Crear colección de configuración contable
    /*const configContable = {
      ruc_empresa: rucEmpresa,
      cuentas: {
        // Cuentas de Remuneraciones
        sueldos: "621101",                  // Sueldos
        asignacion_familiar: "621102",      // Asignación Familiar
        gratificaciones: "621501",          // Gratificaciones
        vacaciones: "621401",               // Vacaciones
        cts: "621201",                      // CTS
        // Cuentas de Aportes del Empleador
        essalud: "627101",                  // ESSALUD
        seguro_vida: "627102",              // Seguro Vida Ley
        sctr: "627103",                     // SCTR
        // Cuentas por Pagar
        remuneraciones_pagar: "411101",     // Remuneraciones por Pagar
        cts_pagar: "411301",                // CTS por Pagar
        essalud_pagar: "403701",            // ESSALUD por Pagar
        onp_pagar: "403201",                // ONP por Pagar
        afp_pagar: "403202",                // AFP por Pagar
        // Cuentas de Gasto de Personal
        cargas_personal: "62",              // Cargas de Personal
      },
      destinos: {
        administrativo: "91",               // Centro de Costo Administrativo
        ventas: "92",                       // Centro de Costo Ventas
        produccion: "93"                    // Centro de Costo Producción
      },
      created_at: new Date(),
      updated_at: new Date()
    };

    await setDoc(doc(db, "pl_config_contable", rucEmpresa), configContable);*/

    // 9. Agregar conceptos contables a conceptos_nomina
    const conceptosContables = [
      {
        codigo: "9101",
        descripcion: "DISTRIBUCIÓN GASTOS ADMINISTRATIVOS",
        tipo: "DISTRIBUCION",
        cuenta_debe: "91",
        cuenta_haber: "79",
        porcentaje: 100,
        ruc_empresa: rucEmpresa
      },
      {
        codigo: "9201",
        descripcion: "DISTRIBUCIÓN GASTOS VENTAS",
        tipo: "DISTRIBUCION",
        cuenta_debe: "92",
        cuenta_haber: "79",
        porcentaje: 100,
        ruc_empresa: rucEmpresa
      },
      {
        codigo: "9301",
        descripcion: "DISTRIBUCIÓN GASTOS PRODUCCIÓN",
        tipo: "DISTRIBUCION",
        cuenta_debe: "93",
        cuenta_haber: "79",
        porcentaje: 100,
        ruc_empresa: rucEmpresa
      }
    ];

    for (const concepto of conceptosContables) {
      await setDoc(doc(db, "pl_conceptos_contables", `${rucEmpresa}_${concepto.codigo}`), concepto);
    }

    const cargaTabla10 = await cargarTabla10Ocupaciones();

    return {
      success: true,
      message: cargaTabla10?.success
        ? "Colecciones inicializadas correctamente y Tabla 10 cargada."
        : "Colecciones inicializadas correctamente (Tabla 10 no se pudo cargar automáticamente).",
      tabla10: cargaTabla10
    };

  } catch (error) {
    console.error("Error al inicializar colecciones:", error);
    return {
      success: false,
      message: "Error al inicializar colecciones",
      error: error.message
    };
  }
};
